import storage from '../../services/storage'
import params from '../../services/params'
import _, { isNil } from 'lodash-es'

export const TICKET_MINIMUM = 1
// If GENERAL_CAP is changed, validation logic for quantity_limit at the production calendar event should also be changed.
// There is also a reference to GENERAL_CAP in rails_helpers.rb that needs to be updated
// Two tests need to be updated (manage_production_calendar_events_spec.rb and purchasing_tickets_spec.rb)
export const GENERAL_CAP = 14
export const EMPLOYEE_CAP = 5

// Business rule is to stop selling a zone after 20 mins, so if it's 3:16pm a user can still buy the 3:00pm zone time for GA
export const ZONE_TIME_CUTOFF = 20

const ticketInGroup = group => ticket => ticket.pricingGroup === group
const isEmployeeTicket = ticket => ticketInGroup('employee')(ticket)

const isDependable = ({ type }) =>
  type.includes('scheduled') || type.includes('addon')

export const hasTicketWith = (
  field,
  fieldValue,
  tickets,
  hasQuantity,
  checkFunc = null,
  every = false
) => {
  const matchFunc = item => {
    let func = item[field] === fieldValue
    if (checkFunc) {
      func = item[field][checkFunc](fieldValue)
    }
    return hasQuantity ? func && item.quantity !== 0 : func
  }
  if (every) {
    return tickets.every(matchFunc)
  }
  return tickets.some(matchFunc)
}

export const ticketMatches = (
  ticketToMatch,
  ageGroup,
  member,
  pricingGroup = null,
  ageGroupIncludes = false
) => {
  if (ageGroupIncludes && pricingGroup) {
    let matches =
      ticketToMatch.member === member &&
      ticketToMatch.pricingGroup === pricingGroup &&
      ageGroup.includes(ticketToMatch.ageGroup)
    return matches
  }
  let matches =
    ticketToMatch.member === member && ticketToMatch.ageGroup === ageGroup
  if (pricingGroup) {
    return matches && ticketToMatch.pricingGroup === pricingGroup
  }
  return matches
}

export const ticketsWithConstituencyPricing = tickets => {
  if (!tickets) return []
  return tickets.filter(ticket => ticket.hasConstituencyPricing)
}

export const hasGenericTickets = tickets =>
  tickets.some(({ ageGroup }) => ageGroup.includes('generic'))

export const hasYouthTickets = tickets =>
  tickets.some(ticket => ticket.ageGroup.includes('youth'))

export const hasChicagoTickets = (tickets, every = false) =>
  hasTicketWith('pricingGroup', 'chicago', tickets, true, null, every)

export const hasIllinoisTickets = (tickets, withQuantity = false) =>
  hasTicketWith('pricingGroup', 'illinois', tickets, withQuantity)

export const hasMemberTickets = tickets =>
  hasTicketWith('member', true, tickets, true)

export const totalTickets = tickets =>
  tickets.reduce((total, current) => (total += current.quantity), 0)

export const isStandalone = event =>
  ['standalone', 'general'].includes(event.type)

export const hitTicketMinimum = ticket => ticket.quantity <= TICKET_MINIMUM

export const hitGeneralAdmissionMax = gaEvent =>
  totalTickets(gaEvent.tickets) >= GENERAL_CAP

export const hitEmployeeMax = (event, remainingEmployeeTickets) => {
  const employeeQuantity = totalTickets(event.tickets.filter(isEmployeeTicket))
  if (remainingEmployeeTickets === null) return employeeQuantity >= EMPLOYEE_CAP
  return employeeQuantity >= remainingEmployeeTickets
}

export const totalMemberTickets = tickets => {
  return totalTickets(
    tickets.filter(ticket => ticket.member && ticket.ageGroup === 'adult')
  )
}

export const hitSpecificConstituencyMax = (
  event,
  remainingConstituencyTickets
) => {
  if (event == null || remainingConstituencyTickets == null) {
    return []
  }

  return remainingConstituencyTickets
    .map(remainingSet => {
      let priceId = remainingSet.priceTypeId
      let constituencyTickets = event.tickets.find(
        t => priceId == (t.id || t.priceTypeId)
      )

      if (!constituencyTickets) {
        return null
      }

      let constituencyTicketsObj = {
        priceTypeId: constituencyTickets.id || constituencyTickets.priceTypeId,
        priceTypeName: constituencyTickets.subLabel,
        quantity: constituencyTickets.quantity
      }

      // RemainingTickets could be negative. This would happen if an admin changes the quantity limit to a smaller number after a user has already purchased tickets.
      if (remainingSet.remainingTickets <= 0) {
        return constituencyTicketsObj
      }

      let totalConstituencyTickets = constituencyTickets?.quantity

      if (
        totalConstituencyTickets &&
        totalConstituencyTickets >= remainingSet.remainingTickets
      ) {
        return constituencyTicketsObj
      }
    })
    .filter(x => !isNil(x))
}

export const hitMembershipMax = (event, remainingMembershipTickets) => {
  if (remainingMembershipTickets == null) {
    return false
  }
  return (
    // Changed to check adult tickets.
    totalMemberTickets(event.tickets) >= remainingMembershipTickets
  )
}

export const clearTicketsAppSessionData = () => {
  ;[
    'fromEventId',
    'step',
    'day',
    'chosenEventId',
    'minimumAvailability',
    'oldCartItemIds',
    'manualMaximum',
    'chosenZoneId'
  ].forEach(key => storage.removeItem(key))
}

export const hasPriceTypes = (priceTypes, event) => {
  if (_.isEmpty(event.pricing)) return false
  const ageGroups = _.isEmpty(event.pricing.nonMember)
    ? Object.values(event.pricing.member)
    : Object.values(event.pricing.nonMember)
  return ageGroups.some(
    group => _.intersection(Object.keys(group.types), priceTypes).length !== 0
  )
}

/**
 * @name extractPricing
 * @desc a function we eventually won't need... deals with all the weird permutations
 * of pricing that an event can have, and gives you something dependable to work with
 * @param event {Object} the target event
 */
// SHEDDWEB-341: Added the selectedEvent section to show the correct pricing object for
// events that have different price types available throughout the day. Ex: free IL resident nights starting at 5pm
export const extractPricing = (event, selectedEvent = null) => {
  const hasChosenZone = !_.isEmpty(event.chosenZone)
  const hasPerformances = !_.isEmpty(event.performances)
  const hasTimes = !_.isEmpty(event.times)

  if (selectedEvent || hasChosenZone) {
    let eventToUse = selectedEvent || event.chosenZone
    return eventToUse.pricing
  }
  if (hasPerformances) return event.performances[0].zones[0].pricing
  if (hasTimes) return event.times[0].pricing
  return event.pricing
}

export const hasMemberPricing = event => _.has(extractPricing(event), 'member')

export const anyMembershipTickets = tickets =>
  tickets.some(ticket => ticket.member) ||
  tickets.some(isEmployeeTicket) ||
  ticketsWithConstituencyPricing(tickets).length > 0

export const isMembershipType = event => event.type === 'membership_plan'

export const notMembershipType = event => !isMembershipType(event)

export const addOnMinimums = (items, event) => {
  let minimums = {
    member: {
      nonResident: {
        adult: 0,
        youth: 0,
        child: 0
      }
    },
    nonMember: {
      resident: {
        adult: 0,
        youth: 0,
        child: 0,
        infant: 0
      },
      nonResident: {
        adult: 0,
        youth: 0,
        child: 0,
        infant: 0
      }
    }
  }

  items.filter(isDependable).forEach(item => {
    item.tickets.forEach(ticket => {
      let minimumGroup
      if (ticket.member) {
        minimumGroup = minimums.member.nonResident
      } else {
        minimumGroup =
          ticket.pricingGroup == 'chicago' || ticket.pricingGroup == 'illinois'
            ? (minimumGroup = minimums.nonMember.resident)
            : (minimumGroup = minimums.nonMember.nonResident)
      }
      const { ageGroup } = ticket

      if (_.has(minimumGroup, ageGroup)) {
        if (ticket.quantity > minimumGroup[ageGroup]) {
          minimumGroup[ticket.ageGroup] = ticket.quantity
        }
      } else {
        minimumGroup[ticket.ageGroup] = ticket.quantity
      }
    })
  })

  if (hasYouthTickets(event.tickets)) {
    const memberTickets = event.tickets.filter(ticket => ticket.member)
    const nonMemberTickets = event.tickets.filter(ticket => !ticket.member)
    _.forIn(minimums, (mins, key) => {
      const allTickets = key === 'member' ? memberTickets : nonMemberTickets

      const addOnQuantity = _.sum(Object.values(mins))
      _.forIn(mins, (quantity, key) => {
        const isTargetTicket = ticket => ticket.ageGroup.includes(key)
        const otherTickets = _.reject(allTickets, isTargetTicket)
        const otherTotal = totalTickets(otherTickets)

        mins[key] = addOnQuantity > otherTotal ? addOnQuantity - otherTotal : 0
      })
    })
  } else if (
    items.some(item => hasYouthTickets(item.tickets)) &&
    !hasYouthTickets(event.tickets)
  ) {
    // this was existing logic to handle the add-on having youth tickets
    // when the base event does not
    minimums.nonMember.adult += minimums.nonMember.youth
    minimums.member.adult += minimums.member.youth
    minimums.nonMember.youth = minimums.member.youth = 0
  }

  return minimums
}

export const findLargestEvent = items =>
  _.maxBy(items, item => _.sumBy(item.tickets, 'quantity'))

/**
 * @name baseEventMinimum
 * @desc calculates whether a given item has any possible dependents,
 * then returns whether the given item has less tickets than its largest dependent
 * @param item {Object} the item being evaluated
 * @param items {Object[]} all items in the cart
 * @returns {boolean}
 */
export const baseEventMinimum = (item, items) => {
  const dependents = items.filter(isDependable)
  if (isDependable(item)) return false // we don't calculate minimums this way for add-ons
  if (dependents.length === 0) return false // base events without add-ons have no dependent minimum

  const baseTickets = item.tickets
  const addOnTickets = findLargestEvent(dependents).tickets

  return totalTickets(baseTickets) <= totalTickets(addOnTickets)
}

/**
 * @name addOnMaximum
 * @desc calculates whether a given item's tickets have met or exceeded the total
 * number of tickets of their largest dependency. infant tickets are filtered out
 * as infants do not need tickets for add-on events.
 * @param item {Object} an "item" in the cart - has properties like `quantityLimit` and `tickets`
 * @param dependencies { Object[] } a list of possible base events for the item. same properties as item.
 * @returns boolean
 */
export const addOnMaximum = (item, dependencies) => {
  if (dependencies.length === 0) return false
  const largestDependency = findLargestEvent(dependencies)

  const baseTickets = ticketsWithoutInfants(item.tickets)
  const addOnTickets = ticketsWithoutInfants(largestDependency.tickets)

  const itemTotal = totalTickets(baseTickets)
  const dependencyTotal = totalTickets(addOnTickets)
  return itemTotal >= dependencyTotal
}

/**
 * predicate for determining whether a ticket is in the "infant" age group
 * @param ticket
 * @returns {boolean}
 */
export const isInfant = ticket => ticket.ageGroup.includes('infant')

/**
 * gets rid of those pesky kids, who throw off calculations for ticket totals
 * @param tickets {Object[]} a list of all the tickets for an event
 * @returns {Object[]} a list of all the tickets with any infant tickets filtered out
 */
export const ticketsWithoutInfants = tickets => _.reject(tickets, isInfant)

export const inManualUpdate = params
  .getAll('event_id', 'date', 'external_id')
  .every(param => param !== null)

export const isChildLearning = pricingGroup => {
  if (!pricingGroup) return false
  return _.isEqual(['adult', 'adult2'].sort(), Object.keys(pricingGroup).sort())
}

export const ticketsChanged = (originalTickets, newTickets) => {
  return originalTickets.some(ticket => {
    let originalTicket = newTickets.findIndex(
      newTicket =>
        newTicket.priceTypeId === ticket.priceTypeId &&
        newTicket.quantity === ticket.quantity
    )
    return originalTicket === -1
  })
}
